import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { useCreateProduct, useUpdateProduct } from "@/hooks/useTrackingProducts";

interface CreateProductModalProps {
  open: boolean;
  onClose: () => void;
  productType: 'gps_device' | 'm2m_chip';
  editingProduct?: any;
}

interface ProductFormData {
  name: string;
  description: string;
  manufacturer: string;
  model: string;
  price_cents: number;
  features: string;
  is_active: boolean;
  stock_quantity: number;
}

export function CreateProductModal({ open, onClose, productType, editingProduct }: CreateProductModalProps) {
  const { register, handleSubmit, reset, setValue, watch, formState: { errors } } = useForm<ProductFormData>();
  const createMutation = useCreateProduct();
  const updateMutation = useUpdateProduct();
  
  const isActive = watch('is_active');
  const isEditing = !!editingProduct;

  useEffect(() => {
    if (editingProduct) {
      setValue('name', editingProduct.name);
      setValue('description', editingProduct.description || '');
      setValue('manufacturer', editingProduct.manufacturer || '');
      setValue('model', editingProduct.model || '');
      setValue('price_cents', editingProduct.price_cents / 100);
      setValue('features', editingProduct.features ? JSON.stringify(editingProduct.features, null, 2) : '');
      setValue('is_active', editingProduct.is_active);
      setValue('stock_quantity', editingProduct.stock_quantity);
    } else {
      reset({
        name: '',
        description: '',
        manufacturer: '',
        model: '',
        price_cents: 0,
        features: '',
        is_active: true,
        stock_quantity: 0,
      });
    }
  }, [editingProduct, reset, setValue]);

  const onSubmit = async (data: ProductFormData) => {
    try {
      let features = null;
      if (data.features) {
        try {
          features = JSON.parse(data.features);
        } catch (e) {
          console.error('Invalid JSON in features');
        }
      }

      const productData = {
        product_type: productType,
        name: data.name,
        description: data.description,
        manufacturer: data.manufacturer,
        model: data.model,
        price_cents: Math.round(data.price_cents * 100),
        features,
        is_active: data.is_active,
        stock_quantity: data.stock_quantity,
        sort_order: 0,
      };

      if (isEditing) {
        await updateMutation.mutateAsync({
          id: editingProduct.id,
          updates: productData,
        });
      } else {
        await createMutation.mutateAsync(productData);
      }
      
      onClose();
    } catch (error) {
      console.error('Error saving product:', error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Editar' : 'Criar'} {productType === 'gps_device' ? 'Dispositivo GPS' : 'Chip M2M'}
          </DialogTitle>
          <DialogDescription>
            Preencha os dados do produto abaixo
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nome *</Label>
              <Input
                id="name"
                {...register('name', { required: 'Nome é obrigatório' })}
                placeholder="Ex: Rastreador GPS TK-303"
              />
              {errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="price_cents">Preço (R$) *</Label>
              <Input
                id="price_cents"
                type="number"
                step="0.01"
                {...register('price_cents', { required: 'Preço é obrigatório', min: 0 })}
                placeholder="0.00"
              />
              {errors.price_cents && <p className="text-sm text-destructive">{errors.price_cents.message}</p>}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Descrição</Label>
            <Textarea
              id="description"
              {...register('description')}
              placeholder="Descrição do produto..."
              rows={3}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="manufacturer">Fabricante</Label>
              <Input
                id="manufacturer"
                {...register('manufacturer')}
                placeholder="Ex: Coban"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="model">Modelo</Label>
              <Input
                id="model"
                {...register('model')}
                placeholder="Ex: TK-303G"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="stock_quantity">Quantidade em Estoque</Label>
            <Input
              id="stock_quantity"
              type="number"
              {...register('stock_quantity', { min: 0 })}
              placeholder="0"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="features">Especificações (JSON)</Label>
            <Textarea
              id="features"
              {...register('features')}
              placeholder='{"gps": "GPS/GLONASS", "bateria": "1000mAh", "resistencia": "IP67"}'
              rows={4}
              className="font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground">Formato JSON válido</p>
          </div>

          <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
            <div>
              <Label htmlFor="is_active" className="text-base">Produto Ativo</Label>
              <p className="text-sm text-muted-foreground">Disponível para venda</p>
            </div>
            <Switch
              id="is_active"
              checked={isActive}
              onCheckedChange={(checked) => setValue('is_active', checked)}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button 
              type="submit" 
              disabled={createMutation.isPending || updateMutation.isPending}
              className="bg-gradient-primary"
            >
              {isEditing ? 'Atualizar' : 'Criar'} Produto
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
